import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from 'styled-components';
import { PageSlice, SliceSlideAnimation, SliceSlideAnimationItem } from '@contracts';

// Register ScrollTrigger with GSAP
gsap.registerPlugin(ScrollTrigger);

// Set default easing and duration for all animations
gsap.defaults({ ease: 'power2.Out', duration: 3 });

interface Props {
  slice: PageSlice;
}

const SlideAnimation: React.FC<Props> = ({ slice }) => {
  const introSlideRef = useRef<HTMLDivElement | null>(null);
  const textBoxOneRef = useRef<HTMLDivElement | null>(null);
  const textBoxTwoRef = useRef<HTMLDivElement | null>(null);
  const textBoxThreeRef = useRef<HTMLDivElement | null>(null);
  const textBoxFourRef = useRef<HTMLDivElement | null>(null);
  const imgOneRef = useRef<HTMLImageElement | null>(null);
  const imgTwoRef = useRef<HTMLImageElement | null>(null);
  const animationContainerRef = useRef<HTMLDivElement | null>(null);

  const sliceDetails = slice.primary as SliceSlideAnimation;
  const sliceItems = slice.items as SliceSlideAnimationItem[];

  // Debounce function, prevents updateAnimation() from being called on every resize event
  function debounce(fn: (...args: any[]) => void, t: number): (...args: any[]) => void {
    let debounceTimer: NodeJS.Timeout;
    return function (...args: any[]): void {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fn(...args), t);
    };
  }

  useEffect(() => {
    // Create the timeline and ScrollTrigger instance
    const tl = gsap.timeline();
    let st: ScrollTrigger;

    const zendeskButton = document.querySelector('.custom-zendesk-button');

    // Function that handles responsive resizing
    // This function is called on resize(debounced) and on initial load
    const updateAnimation = debounce(() => {
      // Clear all inline styles to reset the animation
      gsap.set(textBoxOneRef.current, { clearProps: 'all' });
      gsap.set(textBoxTwoRef.current, { clearProps: 'all' });
      gsap.set(textBoxThreeRef.current, { clearProps: 'all' });
      gsap.set(textBoxFourRef.current, { clearProps: 'all' });
      gsap.set(imgOneRef.current, { clearProps: 'all' });
      gsap.set(imgTwoRef.current, { clearProps: 'all' });

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Save the current progress of the timeline
      const progress = tl.progress();

      /*
      TODO: Explore GSAP's media queries as solution here
      Manually handling different animations for big and small screen sizes,
      this could be possible with ScrollTrigger's media queries but haven't found a way to do it yet

      Also: not using the useDesktop hook for now because it's causing issues.
      Probably because it has it's own resize event handling and it's not working well with the one I'm using here. 
      */
      if (windowWidth > 800) {
        // Window Size > 800px (Animation for big screens)

        // Clear and recreate the timeline
        tl.invalidate()
          .clear()
          .addLabel('Start')
          .to({}, { duration: 1 }) // wait at start, feels better when scrolling
          .addLabel('IntroSlideEnd')
          .fromTo(introSlideRef.current, { opacity: 1 }, { opacity: 0 })
          .fromTo(textBoxOneRef.current, { left: windowWidth * -1, opacity: 0 }, { left: '0', opacity: 1 }, '>-1.5')
          .fromTo(imgOneRef.current, { left: windowWidth * 1 }, { left: windowWidth * 0.5 }, '<')
          .addLabel('SlideOneEnd')
          .to(imgOneRef.current, { left: 0 }, '>')
          .to(textBoxOneRef.current, { opacity: 0 }, '<')
          .fromTo(textBoxTwoRef.current, { left: windowWidth * 1 }, { left: windowWidth * 0.5 }, '<')
          .addLabel('SlideTwoEnd')
          .fromTo(textBoxTwoRef.current, { opacity: 1 }, { opacity: 0 }, '>')
          .to(imgOneRef.current, { left: windowWidth * -0.5 }, '<')
          .fromTo(textBoxThreeRef.current, { left: windowWidth * -1, opacity: 0 }, { left: '0', opacity: 1 }, '<')
          .fromTo(imgTwoRef.current, { left: windowWidth * 1 }, { left: windowWidth * 0.5 }, '<')
          .addLabel('SlideThreeEnd')
          .to(textBoxThreeRef.current, { opacity: 0 }, '>')
          .to(imgTwoRef.current, { left: 0 }, '<')
          .fromTo(textBoxFourRef.current, { left: windowWidth * 1, opacity: 1 }, { left: windowWidth * 0.5, opacity: 1 }, '<')
          .addLabel('SlideFourEnd')
          .to({}, { duration: 1 }); // wait at final slide, feels better when scrolling
      } else {
        // Window Size <= 800px (Animation for small screens)

        // Clear and recreate the timeline
        tl.invalidate()
          .clear()
          .addLabel('Start')
          .to({}, { duration: 1 }) // wait at start, feels better when scrolling
          .addLabel('IntroSlideEnd')
          .fromTo(introSlideRef.current, { opacity: 1 }, { opacity: 0 })
          .fromTo(textBoxOneRef.current, { opacity: 0 }, { opacity: 1 }, '>-1.5')
          .fromTo(imgOneRef.current, { top: windowHeight * -1, opacity: 0 }, { top: '0', opacity: 1 }, '<')
          .addLabel('SlideOneEnd')
          .to(textBoxOneRef.current, { top: windowHeight }, '>')
          .fromTo(textBoxTwoRef.current, { top: windowHeight * -0.6 }, { top: windowHeight * 0.4 }, '<')
          .addLabel('SlideTwoEnd')
          .fromTo(textBoxTwoRef.current, { top: windowHeight * 0.4, opacity: 1 }, { top: windowHeight, opacity: 0 }, '>+1')
          .to(imgOneRef.current, { top: windowHeight * -1, opacity: 0 }, '<')
          .fromTo(textBoxThreeRef.current, { opacity: 0 }, { opacity: 1 }, '<+0.5')
          .fromTo(imgTwoRef.current, { top: windowHeight * -1, opacity: 0 }, { top: '0', opacity: 1 }, '<')
          .addLabel('SlideThreeEnd')
          .to(textBoxThreeRef.current, { top: windowHeight }, '>+1')
          .fromTo(textBoxFourRef.current, { top: windowHeight * -0.6, opacity: 0 }, { top: windowHeight * 0.4, opacity: 1 }, '<')
          .addLabel('SlideFourEnd')
          .to({}, { duration: 1 }); // wait at final slide, feels better when scrolling
      }

      // Set the progress of the timeline back to what it was before
      tl.progress(progress);

      // Refresh the ScrollTrigger instance
      if (st) {
        st.refresh();
      }
    }, 200); // Set the debounce time to 200ms

    window.addEventListener('resize', updateAnimation);

    // Call once to set the initial properties
    updateAnimation();
    // Create ScrollTrigger instance - should be after tl is created
    ScrollTrigger.config({
      ignoreMobileResize: true
    });
    st = ScrollTrigger.create({
      id: 'stAnimation',
      trigger: animationContainerRef.current,
      start: 'top top',
      end: '+=5000',
      pin: true,
      animation: tl,
      scrub: 1,
      snap: {
        snapTo: 'labelsDirectional',
        delay: 0,
        ease: 'power2.Out'
      },
      // markers: true,
      invalidateOnRefresh: true,
      onToggle: (self) => {
        if (self.isActive && zendeskButton) {
          (zendeskButton as HTMLElement).style.display = 'none';
        } else if (zendeskButton) {
          (zendeskButton as HTMLElement).style.display = 'block';
        }
      }
    });
    return () => window.removeEventListener('resize', updateAnimation);
  }, []);

  /* 
  For now I want exactly 2 images and 4 text boxes
  Could be extended in the future to support a flexible number of images and text boxes
  If there are not exactly 2 images and 4 text boxes, return null 
  TODO: Make the slides number flexible
  */
  const textBoxComponents = [TextBoxOne, TextBoxTwo, TextBoxThree, TextBoxFour];
  const textBoxRefs = [textBoxOneRef, textBoxTwoRef, textBoxThreeRef, textBoxFourRef];
  if (sliceDetails?.anim_image_one.url === undefined) {
    console.error('SlideAnimationSlice First animated image is not defined');
    return null;
  }
  if (sliceDetails?.anim_image_two.url === undefined) {
    console.error('SlideAnimationSlice Second animated image is not defined');
    return null;
  }
  if (sliceItems?.length !== 4) {
    console.error('SlideAnimationSlice Number of text box elements defined is not 4');
    return null;
  }
  return (
    <SlideAnimationContainer ref={animationContainerRef}>
      <Wrapper>
        <IntroSlide ref={introSlideRef}>
          <div>
            <h2>{sliceDetails.intro_heading.text}</h2>
            <p>{sliceDetails.intro_subheading}</p>
          </div>
        </IntroSlide>
        <ImgOne data-testid="image-one" src={sliceDetails.anim_image_one.url} alt={sliceDetails.anim_image_one.alt} ref={imgOneRef}></ImgOne>
        <ImgTwo data-testid="image-two" src={sliceDetails.anim_image_two.url} alt={sliceDetails.anim_image_two.alt} ref={imgTwoRef}></ImgTwo>

        {sliceItems.map((item: any, index: number) => {
          const TextBoxComponent = textBoxComponents[index];
          const textBoxRef = textBoxRefs[index];

          return (
            <TextBoxComponent key={index} ref={textBoxRef}>
              <div>
                <h2>{item.textbox_title.text}</h2>
                <h3>{item.big_number}</h3>
                <p>{item.textbox_description.text}</p>
              </div>
            </TextBoxComponent>
          );
        })}
      </Wrapper>
    </SlideAnimationContainer>
  );
};
export default SlideAnimation;

const SlideAnimationContainer = styled.section`
  background: #fdf3de;
  display: flex;
  height: 100vh;
  justify-content: center;
  padding-top: 68px;
  overflow: hidden;
`;

const Wrapper = styled.div`
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
  padding: 0 4rem;
  position: relative;
  width: 100%;
`;

const BaseSlideStyle = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const BaseTextBoxStyle = styled.div`
  background: transparent;
  opacity: 1;
  position: absolute;
  z-index: 1;
  width: 50%;
  top: 0;
  left: 0;
  height: 100%;
  display: flex;
  align-items: center;

  @media (max-width: 800px) {
    top: 40%;
    width: 100%;
    height: 60%;
    align-items: center;
    padding: 1rem 0 4rem 0;
    display: flex;
  }

  & h2 {
    font-family: 'Domaine';
    font-size: 3.5rem;
    margin-bottom: 0;

    @media (max-width: 1280px) {
      font-size: 2.5rem;
      margin-bottom: 0;
    }

    @media (max-width: 800px) {
      font-size: 1.75rem;
    }
  }

  & h3 {
    color: #eba920;
    font-family: 'Domaine';
    font-size: 6rem;
    margin: 0;
    padding: 0;

    @media (max-width: 800px) {
      font-size: 4rem;
      margin-bottom: 0;
    }
    @media (max-width: 600px) {
      font-size: 3rem;
      margin-bottom: 0;
    }
  }

  & > div {
    padding: 0 20%;

    @media (max-width: 1280px) {
      padding: 0 10%;
    }
  }
`;

const BaseImgStyle = styled.img`
  background: black;
  height: 100vh;
  opacity: 1;
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  z-index: 2;
  object-fit: cover;

  @media (max-width: 800px) {
    width: 100%;
    height: 40%;
    opacity: 0;
    object-fit: contain;
  }
`;

const IntroSlide = styled(BaseSlideStyle).attrs({ as: 'div' })`
  margin: 0 auto;
  max-width: 1200px;
  align-items: center;
  justify-content: center;
  height: 100%;

  & > div {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  & h2 {
    font-family: 'Domaine';
    font-size: 3rem;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
    letter-spacing: -1.28px;
    margin: 0;
    margin-bottom: 7rem;
    padding: 0;
    text-align: center;

    @media (max-width: 600px) {
      font-size: 2rem;
    }
  }
`;

const TextBoxOne = styled(BaseTextBoxStyle).attrs({ as: 'div' })``;
const TextBoxTwo = styled(BaseTextBoxStyle).attrs({ as: 'div' })``;
const TextBoxThree = styled(BaseTextBoxStyle).attrs({ as: 'div' })``;
const TextBoxFour = styled(BaseTextBoxStyle).attrs({ as: 'div' })``;

const ImgOne = styled(BaseImgStyle).attrs({ as: 'img' })`
  background: #f1b3a8;
`;

const ImgTwo = styled(BaseImgStyle).attrs({ as: 'img' })`
  background: #dce9f2;
`;
